import { StoreDataSourceEnum } from '@inter-ikea-kompis/enums';
import { ServiceException } from '@inter-ikea-kompis/exceptions';
import type { IStore } from '@inter-ikea-kompis/types';
import type { CacheStore } from '@inter-ikea-kompis/utilities';
import { GlobalCacheStore } from '@inter-ikea-kompis/utilities';

import CacheStoreType from '../../enums/CacheStoreType.js';
import DexfStoreService from '../../services/store/DexfStoreService.js';
import IowsStoreService from '../../services/store/IowsStoreService.js';
import DexfConfiguration from '../../utilities/DexfConfiguration.js';
import type IStoreServiceOptions from './IStoreServiceOptions.js';
import StoreCache from './StoreCache.js';

/**
 * This class is used as a simple interface for store services.
 */
export default class StoreService {
	private readonly dexfConfiguration: DexfConfiguration;
	private readonly consumer?: string;
	private readonly contract?: string;
	private readonly locale: string;
	private readonly cache: CacheStore;

	/**
	 * Constructor.
	 *
	 * @param options Options.
	 */
	constructor(options: IStoreServiceOptions) {
		this.dexfConfiguration = new DexfConfiguration(options);
		this.locale = options.locale;
		this.consumer = options.consumer;
		this.contract = options.contract;
		this.cache = options.cache || new GlobalCacheStore();
	}
	/**
	 * Fetches and returns translations.
	 *
	 * @returns Promise object.
	 */
	public async getStores(): Promise<IStore[]> {
		const cacheStore = this.cache.getStore(CacheStoreType.store);
		const storeCache = new StoreCache(this.locale, this.cache);
		const queueCacheKey = this.locale + 'queue';
		const cachedStores = storeCache.getStores();

		if (cachedStores) {
			return cachedStores;
		}

		const cachedQueue = cacheStore.get(queueCacheKey);

		if (cachedQueue) {
			return new Promise((resolve) => cachedQueue.push(resolve));
		}

		const newQueue: { (data: IStore[]): void }[] = [];

		cacheStore.set(queueCacheKey, newQueue);

		const service = await this.getService();
		const stores = await service.getStores();

		storeCache.addStores(stores);
		cacheStore.delete(queueCacheKey);

		for (const callback of newQueue) {
			callback(stores);
		}

		return stores;
	}

	/**
	 * Returns service.
	 *
	 * @returns Service.
	 */
	private async getService(): Promise<IowsStoreService | DexfStoreService> {
		const { kompis } = await this.dexfConfiguration.getSettings();

		switch (kompis.serviceSettings.storeDataSource) {
			case StoreDataSourceEnum.dexf: {
				const dexfApiKey = await this.dexfConfiguration.getDexfApiKeyOrThrow();
				return new DexfStoreService({
					endpoint: kompis.endpoints.dexfStoreService,
					dexfApiKey: dexfApiKey
				});
			}
			default:
				if (!this.consumer || !this.contract) {
					throw new ServiceException('Consumer and contract must be defined.');
				}
				return new IowsStoreService({
					consumer: this.consumer,
					contract: this.contract,
					endpoint: kompis.endpoints.iowsStoreService
				});
		}
	}
}
